import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  cn,
} from "@exhousing/design-system";
import { FileText, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { StatusBadge } from "@/components/StatusBadge";
import { useContracts, useCreateContract } from "@/hooks/api/useContracts";
import { useTenants } from "@/hooks/api/useParties";
import { useProperties } from "@/hooks/api/useProperties";

function euro(v: number | null | undefined): string {
  if (v == null) return "—";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(v);
}

export function ContractsPage() {
  const { data: contracts } = useContracts();
  const { data: properties } = useProperties();
  const { data: tenants } = useTenants();
  const create = useCreateContract();
  const [showForm, setShowForm] = useState(false);
  const [propertyId, setPropertyId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [startDate, setStartDate] = useState("");

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!propertyId) return;
    create.mutate(
      {
        property_id: propertyId,
        tenant_ids: tenantId ? [tenantId] : [],
        term: "definite",
        lease_variant: "roz",
        start_date: startDate || null,
        bank_guarantee: false,
        rent_from_company: false,
        commission_type: "percentage",
        commission_value: 0,
        commission_vat_rate: 0.21,
        leasing_fee: 0,
        leasing_fee_vat_rate: 0.21,
      },
      {
        onSuccess: () => {
          toast.success("Contract created (pricing snapshotted from the property)");
          setShowForm(false);
          setPropertyId("");
          setTenantId("");
          setStartDate("");
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const selectClass =
    "flex h-11 w-full rounded-md border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Contracts</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {contracts ? `${contracts.length} contracts` : "Loading…"}
          </p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)}>
          <Plus className="size-4" />
          New contract
        </Button>
      </header>

      {showForm && (
        <Card className="animate-fade-in-up">
          <CardContent className="pt-6">
            <form onSubmit={onCreate} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="property">Property</Label>
                <select
                  id="property"
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                  className={selectClass}
                >
                  <option value="">Select a property…</option>
                  {(properties ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {[p.street, p.house_number].filter(Boolean).join(" ") ||
                        p.city ||
                        p.id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tenant">Primary tenant (optional)</Label>
                <select
                  id="tenant"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  className={selectClass}
                >
                  <option value="">No tenant yet</option>
                  {(tenants ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.contact.display_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="start_date">Start date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-11"
                />
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <Button type="submit" disabled={create.isPending || !propertyId}>
                  {create.isPending ? "Creating…" : "Create contract"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {contracts && contracts.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-6 py-3 font-medium">Property</th>
                  <th className="px-6 py-3 font-medium">Tenant(s)</th>
                  <th className="px-6 py-3 font-medium">Landlord</th>
                  <th className="px-6 py-3 font-medium">Period</th>
                  <th className="px-6 py-3 font-medium tabular-nums">Rent</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((c) => (
                  <tr key={c.id} className="border-b border-border/60 last:border-0">
                    <td className="px-6 py-3">
                      <Link to={`/contracts/${c.id}`} className="hover:text-primary">
                        <span className="font-medium">
                          {c.property_address ?? c.reference ?? "Contract"}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {c.city ?? `Ref ${c.id.slice(0, 8)}`}
                        </span>
                      </Link>
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">
                      {c.tenants ?? "—"}
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">
                      {c.landlord_name ?? "—"}
                    </td>
                    <td className="px-6 py-3 text-xs text-muted-foreground">
                      {c.start_date ?? "—"}
                      {c.end_date ? ` → ${c.end_date}` : ""}
                    </td>
                    <td className="px-6 py-3 tabular-nums">{euro(c.rental_price)}</td>
                    <td className="px-6 py-3">
                      <StatusBadge status={c.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
              <FileText className={cn("size-8 opacity-40")} />
              <p className="text-sm">No contracts yet.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
